/**
 * useTelegram / usePlatform — Universal Multi-Platform Hook
 * 
 * Поддерживает:
 * - Telegram WebApp SDK
 * - MAX Messenger WebApp SDK
 * - Standalone Web (Browser Dev / Preview)
 *
 * Предоставляет доступ к:
 * - initData (для авторизации на сервере)
 * - themeParams & isDarkMode
 * - BackButton, HapticFeedback
 * - User info & Platform identifier
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PlatformType, PlatformUserDTO, PlatformThemeParamsDTO } from '@tg-miniapp/shared-types';
import { getPlatformBridge, detectPlatform, type IPlatformBridge } from '../services/platform-bridge';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

export function useTelegram() {
  const [bridge, setBridge] = useState<IPlatformBridge>(() => getPlatformBridge());
  const [platform, setPlatform] = useState<PlatformType>(() => detectPlatform());

  // Telegram WebApp direct object reference for legacy compatibility
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;

  // Polling / detection during initialization in async WebViews (iOS WebKit / Android)
  useEffect(() => {
    bridge.ready();
    bridge.expand();

    const checkInterval = setInterval(() => {
      const activePlatform = detectPlatform();
      if (activePlatform !== platform) {
        setPlatform(activePlatform);
        const newBridge = getPlatformBridge();
        setBridge(newBridge);
        newBridge.ready();
        newBridge.expand();
      }
    }, 100);

    const timer = setTimeout(() => clearInterval(checkInterval), 3000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timer);
    };
  }, [bridge, platform]);

  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>(bridge.colorScheme);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      const handleTgTheme = () => setColorScheme(window.Telegram?.WebApp?.colorScheme || 'light');
      window.Telegram.WebApp.onEvent('themeChanged', handleTgTheme);
      return () => {
        window.Telegram?.WebApp?.offEvent('themeChanged', handleTgTheme);
      };
    }
    return undefined;
  }, []);

  const isDarkMode = useMemo(() => colorScheme === 'dark', [colorScheme]);

  const user = useMemo((): TelegramUser | null => {
    if (bridge.user) {
      return {
        id: typeof bridge.user.rawId === 'number' ? bridge.user.rawId : Number(bridge.user.rawId) || 1088744046,
        first_name: bridge.user.firstName,
        last_name: bridge.user.lastName,
        username: bridge.user.username,
        language_code: bridge.user.languageCode,
        is_premium: bridge.user.isPremium
      };
    }
    return null;
  }, [bridge]);

  const platformUser: PlatformUserDTO | null = bridge.user;

  const initData = useMemo(() => {
    return bridge.initData;
  }, [bridge]);

  const showBackButton = useCallback((onBack: () => void) => {
    return bridge.showBackButton(onBack);
  }, [bridge]);

  const haptic = useMemo(() => ({
    impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light') => {
      bridge.hapticImpact(style);
    },
    success: () => bridge.hapticNotification('success'),
    error: () => bridge.hapticNotification('error'),
    warning: () => bridge.hapticNotification('warning'),
    selection: () => bridge.hapticSelection(),
  }), [bridge]);

  return {
    tg,
    bridge,
    platform,
    user,
    platformUser,
    isDarkMode,
    initData,
    showBackButton,
    haptic,
    isInTelegram: platform === 'telegram',
    isInMax: platform === 'max',
    isStandaloneWeb: platform === 'web'
  };
}

export const usePlatform = useTelegram;
